import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Check, Undo2, Pencil, Wand2, Minus, Spline, Type } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Lightweight, iOS-style image markup editor. Vector shape model rendered to a
// <canvas>; exports a flattened JPEG blob. Tools: freehand draw, smooth draw
// (auto-smooths on release), straight line, curved line (drag start / middle /
// end handles to shape it), and text. Lines/curves support solid or dashed
// styles and an optional arrowhead.
// ─────────────────────────────────────────────────────────────────────────────

type Tool = 'draw' | 'smooth' | 'line' | 'curve' | 'text';
type CurveHandle = 'a' | 'b' | 'mid';
interface Pt { x: number; y: number }

interface PathShape { id: number; kind: 'path'; pts: Pt[]; color: string; width: number; smooth: boolean }
interface LineShape { id: number; kind: 'line'; a: Pt; b: Pt; color: string; width: number; dash: boolean; arrow: boolean }
interface CurveShape { id: number; kind: 'curve'; a: Pt; b: Pt; c: Pt; color: string; width: number; dash: boolean; arrow: boolean }
interface TextShape { id: number; kind: 'text'; x: number; y: number; text: string; color: string; size: number }
type Shape = PathShape | LineShape | CurveShape | TextShape;

const COLORS = ['#FF3B30', '#FF9500', '#FFCC00', '#34C759', '#007AFF', '#AF52DE', '#000000', '#FFFFFF'];
const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';

let SHAPE_ID = 1;

/** Drop points closer together than `minDist` to remove finger jitter. */
function decimate(pts: Pt[], minDist: number): Pt[] {
  if (pts.length < 3) return pts;
  const out: Pt[] = [pts[0]];
  for (let i = 1; i < pts.length - 1; i++) {
    const last = out[out.length - 1];
    if (Math.hypot(pts[i].x - last.x, pts[i].y - last.y) >= minDist) out.push(pts[i]);
  }
  out.push(pts[pts.length - 1]);
  return out;
}

/** Stroke an arrowhead whose tip is at `tip`, pointing away from `from`. */
function drawArrowhead(ctx: CanvasRenderingContext2D, tip: Pt, from: Pt, len: number) {
  const angle = Math.atan2(tip.y - from.y, tip.x - from.x);
  const spread = Math.PI / 7;
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.moveTo(tip.x, tip.y);
  ctx.lineTo(tip.x - len * Math.cos(angle - spread), tip.y - len * Math.sin(angle - spread));
  ctx.moveTo(tip.x, tip.y);
  ctx.lineTo(tip.x - len * Math.cos(angle + spread), tip.y - len * Math.sin(angle + spread));
  ctx.stroke();
}

/** Draw a shape onto a context. `scale`/`origin` map natural→target coords. */
function drawShape(ctx: CanvasRenderingContext2D, s: Shape, scale: number, origin: Pt) {
  const T = (p: Pt): Pt => ({ x: origin.x + p.x * scale, y: origin.y + p.y * scale });

  if (s.kind === 'text') {
    if (!s.text) return;
    ctx.setLineDash([]);
    ctx.fillStyle = s.color;
    const fontPx = Math.max(8, s.size * scale);
    ctx.font = `600 ${fontPx}px ${FONT}`;
    ctx.textBaseline = 'top';
    const tp = T({ x: s.x, y: s.y });
    s.text.split('\n').forEach((ln, i) => ctx.fillText(ln, tp.x, tp.y + i * fontPx * 1.2));
    return;
  }

  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = s.color;
  ctx.lineWidth = Math.max(1, s.width * scale);
  const dash = (s.kind === 'line' || s.kind === 'curve') && s.dash;
  ctx.setLineDash(dash ? [s.width * 2.4 * scale, s.width * 2.2 * scale] : []);
  ctx.beginPath();
  if (s.kind === 'path') {
    if (!s.pts.length) return;
    const tp = s.pts.map(T);
    ctx.moveTo(tp[0].x, tp[0].y);
    if (s.smooth && tp.length > 2) {
      for (let i = 1; i < tp.length - 1; i++) {
        const mx = (tp[i].x + tp[i + 1].x) / 2;
        const my = (tp[i].y + tp[i + 1].y) / 2;
        ctx.quadraticCurveTo(tp[i].x, tp[i].y, mx, my);
      }
      const n1 = tp[tp.length - 2];
      const n = tp[tp.length - 1];
      ctx.quadraticCurveTo(n1.x, n1.y, n.x, n.y);
    } else {
      for (let i = 1; i < tp.length; i++) ctx.lineTo(tp[i].x, tp[i].y);
    }
    ctx.stroke();
  } else if (s.kind === 'line') {
    const a = T(s.a);
    const b = T(s.b);
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
    if (s.arrow) drawArrowhead(ctx, b, a, Math.max(9, s.width * scale * 4));
  } else {
    const a = T(s.a);
    const b = T(s.b);
    const c = T(s.c);
    ctx.moveTo(a.x, a.y);
    ctx.quadraticCurveTo(c.x, c.y, b.x, b.y);
    ctx.stroke();
    if (s.arrow) {
      const from = Math.hypot(c.x - b.x, c.y - b.y) > 1 ? c : a;
      drawArrowhead(ctx, b, from, Math.max(9, s.width * scale * 4));
    }
  }
  ctx.setLineDash([]);
}

/** On-curve midpoint (t=0.5) of a quadratic curve. */
function curveMid(s: CurveShape): Pt {
  return { x: 0.25 * s.a.x + 0.5 * s.c.x + 0.25 * s.b.x, y: 0.25 * s.a.y + 0.5 * s.c.y + 0.25 * s.b.y };
}

export default function ImageEditor({
  src,
  onCancel,
  onSave,
}: {
  src: string;
  onCancel: () => void;
  onSave: (blob: Blob) => void | Promise<void>;
}) {
  const stageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const viewRef = useRef<{ scale: number; origin: Pt }>({ scale: 1, origin: { x: 0, y: 0 } });
  const draftRef = useRef<Shape | null>(null);
  const draggingHandleRef = useRef<CurveHandle | null>(null);

  const [imgReady, setImgReady] = useState(false);
  const [tool, setTool] = useState<Tool>('draw');
  const [color, setColor] = useState('#FF3B30');
  const [sizeIdx, setSizeIdx] = useState(1);
  const [dash, setDash] = useState(false);
  const [arrow, setArrow] = useState(false);
  const [shapes, setShapes] = useState<Shape[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [editingTextId, setEditingTextId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmExit, setConfirmExit] = useState(false);
  const [tick, setTick] = useState(0); // forces redraw during drafting

  const natural = () => {
    const img = imgRef.current;
    return { w: img?.naturalWidth || 1, h: img?.naturalHeight || 1 };
  };

  const widthFor = useCallback((idx: number) => {
    const { w, h } = natural();
    const unit = Math.max(w, h) / 260;
    return unit * [1.2, 2.3, 3.8][idx];
  }, []);
  const textSizeFor = useCallback((idx: number) => widthFor(idx) * 5, [widthFor]);

  // Load the image (fetch → blob to avoid tainting the export canvas on CORS).
  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;
    const finish = (img: HTMLImageElement) => {
      if (cancelled) return;
      imgRef.current = img;
      setImgReady(true);
    };
    (async () => {
      try {
        const res = await fetch(src);
        const blob = await res.blob();
        objectUrl = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => finish(img);
        img.onerror = () => fallback();
        img.src = objectUrl;
      } catch {
        fallback();
      }
    })();
    const fallback = () => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => finish(img);
      img.src = src;
    };
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  // Compute the contain-fit rect for the current container size.
  const layout = useCallback(() => {
    const stage = stageRef.current;
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!stage || !canvas || !img) return;
    const dpr = window.devicePixelRatio || 1;
    const cw = stage.clientWidth;
    const ch = stage.clientHeight;
    canvas.width = Math.round(cw * dpr);
    canvas.height = Math.round(ch * dpr);
    canvas.style.width = `${cw}px`;
    canvas.style.height = `${ch}px`;
    const nW = img.naturalWidth;
    const nH = img.naturalHeight;
    const scale = Math.min(cw / nW, ch / nH);
    const dispW = nW * scale;
    const dispH = nH * scale;
    viewRef.current = { scale, origin: { x: (cw - dispW) / 2, y: (ch - dispH) / 2 } };
  }, []);

  const drawHandle = (ctx: CanvasRenderingContext2D, sx: number, sy: number, r: number) => {
    ctx.setLineDash([]);
    ctx.beginPath();
    ctx.arc(sx, sy, r, 0, Math.PI * 2);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = '#007AFF';
    ctx.stroke();
  };

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const { scale, origin } = viewRef.current;
    ctx.drawImage(img, origin.x, origin.y, img.naturalWidth * scale, img.naturalHeight * scale);
    for (const s of shapes) {
      if (s.kind === 'text' && s.id === editingTextId) continue; // shown via overlay input
      drawShape(ctx, s, scale, origin);
    }
    if (draftRef.current) drawShape(ctx, draftRef.current, scale, origin);
    // Curve handles for the active curve: start, middle, end.
    const active = shapes.find((s) => s.id === activeId);
    if (active && active.kind === 'curve') {
      const mid = curveMid(active);
      drawHandle(ctx, origin.x + active.a.x * scale, origin.y + active.a.y * scale, 7);
      drawHandle(ctx, origin.x + active.b.x * scale, origin.y + active.b.y * scale, 7);
      drawHandle(ctx, origin.x + mid.x * scale, origin.y + mid.y * scale, 9);
    }
  }, [shapes, activeId, editingTextId]);

  useEffect(() => {
    if (!imgReady) return;
    layout();
    redraw();
    const ro = new ResizeObserver(() => {
      layout();
      redraw();
    });
    if (stageRef.current) ro.observe(stageRef.current);
    return () => ro.disconnect();
  }, [imgReady, layout, redraw]);

  useEffect(() => {
    redraw();
  }, [redraw, tick]);

  // ── Pointer interaction ────────────────────────────────────────────────────
  const toNatural = (clientX: number, clientY: number): Pt => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const { scale, origin } = viewRef.current;
    const { w, h } = natural();
    const x = (clientX - rect.left - origin.x) / scale;
    const y = (clientY - rect.top - origin.y) / scale;
    return { x: Math.max(0, Math.min(w, x)), y: Math.max(0, Math.min(h, y)) };
  };

  const screenOf = (p: Pt): Pt => {
    const { scale, origin } = viewRef.current;
    return { x: origin.x + p.x * scale, y: origin.y + p.y * scale };
  };

  const beginTextEdit = (shapeId: number) => {
    setEditingTextId(shapeId);
    setActiveId(shapeId);
    setTimeout(() => setTick((t) => t + 1), 0);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (!imgReady) return;
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    const p = toNatural(e.clientX, e.clientY);
    const { scale } = viewRef.current;

    // Text tool: edit an existing text under the finger, else place a new one.
    if (tool === 'text') {
      const hit = [...shapes].reverse().find((s) => {
        if (s.kind !== 'text') return false;
        const estW = Math.max(s.size * 1.2, (s.text || ' ').length * s.size * 0.6);
        return p.x >= s.x - s.size * 0.3 && p.x <= s.x + estW && p.y >= s.y - s.size * 0.3 && p.y <= s.y + s.size * 1.4;
      }) as TextShape | undefined;
      if (hit) {
        beginTextEdit(hit.id);
        return;
      }
      const ts: TextShape = { id: SHAPE_ID++, kind: 'text', x: p.x, y: p.y, text: '', color, size: textSizeFor(sizeIdx) };
      setShapes((prev) => [...prev, ts]);
      beginTextEdit(ts.id);
      return;
    }

    // Grab one of the active curve's handles (start / middle / end)?
    const active = shapes.find((s) => s.id === activeId);
    if (active && active.kind === 'curve') {
      const mid = curveMid(active);
      const candidates: { h: CurveHandle; pt: Pt }[] = [
        { h: 'mid', pt: mid },
        { h: 'a', pt: active.a },
        { h: 'b', pt: active.b },
      ];
      for (const cand of candidates) {
        const sp = screenOf(cand.pt);
        const fp = screenOf(p);
        if (Math.hypot(sp.x - fp.x, sp.y - fp.y) < 24) {
          draggingHandleRef.current = cand.h;
          return;
        }
      }
    }

    const common = { id: SHAPE_ID++, color, width: widthFor(sizeIdx) };
    if (tool === 'draw' || tool === 'smooth') {
      draftRef.current = { ...common, kind: 'path', pts: [p], smooth: tool === 'smooth' };
    } else if (tool === 'line') {
      draftRef.current = { ...common, kind: 'line', a: p, b: p, dash, arrow };
    } else {
      draftRef.current = { ...common, kind: 'curve', a: p, b: p, c: p, dash, arrow };
    }
    setActiveId(null);
    setTick((t) => t + 1);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const handle = draggingHandleRef.current;
    if (handle) {
      const p = toNatural(e.clientX, e.clientY);
      setShapes((prev) =>
        prev.map((s) => {
          if (s.id !== activeId || s.kind !== 'curve') return s;
          if (handle === 'a') return { ...s, a: p };
          if (handle === 'b') return { ...s, b: p };
          return { ...s, c: { x: 2 * p.x - 0.5 * (s.a.x + s.b.x), y: 2 * p.y - 0.5 * (s.a.y + s.b.y) } };
        }),
      );
      return;
    }
    const d = draftRef.current;
    if (!d) return;
    const p = toNatural(e.clientX, e.clientY);
    if (d.kind === 'path') {
      d.pts.push(p);
    } else if (d.kind === 'line') {
      d.b = p;
    } else if (d.kind === 'curve') {
      d.b = p;
      d.c = { x: (d.a.x + p.x) / 2, y: (d.a.y + p.y) / 2 };
    }
    setTick((t) => t + 1);
  };

  const onPointerUp = () => {
    if (draggingHandleRef.current) {
      draggingHandleRef.current = null;
      return;
    }
    const d = draftRef.current;
    draftRef.current = null;
    if (!d) return;
    if (d.kind === 'path') {
      if (d.smooth) d.pts = decimate(d.pts, Math.max(2, d.width * 1.1));
      if (d.pts.length < 2) {
        setTick((t) => t + 1);
        return;
      }
      setShapes((prev) => [...prev, d]);
    } else if (d.kind === 'line' || d.kind === 'curve') {
      if (Math.hypot(d.b.x - d.a.x, d.b.y - d.a.y) < 3) {
        setTick((t) => t + 1);
        return;
      }
      setShapes((prev) => [...prev, d]);
      setActiveId(d.id);
    }
  };

  // Color / size / dash / arrow also update the currently-selected shape.
  const applyColor = (c: string) => {
    setColor(c);
    if (activeId != null) setShapes((prev) => prev.map((s) => (s.id === activeId ? { ...s, color: c } : s)));
  };
  const applySize = (idx: number) => {
    setSizeIdx(idx);
    if (activeId != null)
      setShapes((prev) =>
        prev.map((s) => {
          if (s.id !== activeId) return s;
          return s.kind === 'text' ? { ...s, size: textSizeFor(idx) } : { ...s, width: widthFor(idx) };
        }),
      );
  };
  const applyDash = (val: boolean) => {
    setDash(val);
    if (activeId != null)
      setShapes((prev) => prev.map((s) => (s.id === activeId && (s.kind === 'line' || s.kind === 'curve') ? { ...s, dash: val } : s)));
  };
  const applyArrow = (val: boolean) => {
    setArrow(val);
    if (activeId != null)
      setShapes((prev) => prev.map((s) => (s.id === activeId && (s.kind === 'line' || s.kind === 'curve') ? { ...s, arrow: val } : s)));
  };

  const updateText = (id: number, text: string) =>
    setShapes((prev) => prev.map((s) => (s.id === id && s.kind === 'text' ? { ...s, text } : s)));

  const commitText = () => {
    const id = editingTextId;
    setEditingTextId(null);
    if (id == null) return;
    setShapes((prev) => prev.filter((s) => !(s.id === id && s.kind === 'text' && !s.text.trim())));
    setTick((t) => t + 1);
  };

  const undo = () => {
    setShapes((prev) => {
      const next = prev.slice(0, -1);
      const removed = prev[prev.length - 1];
      if (removed && removed.id === activeId) setActiveId(null);
      return next;
    });
  };

  const handleCancel = () => {
    if (shapes.length > 0) setConfirmExit(true);
    else onCancel();
  };

  const handleSave = async () => {
    const img = imgRef.current;
    if (!img) return;
    commitText();
    setSaving(true);
    try {
      const out = document.createElement('canvas');
      out.width = img.naturalWidth;
      out.height = img.naturalHeight;
      const ctx = out.getContext('2d');
      if (!ctx) throw new Error('no ctx');
      ctx.drawImage(img, 0, 0);
      for (const s of shapes) {
        if (s.kind === 'text' && !s.text.trim()) continue;
        drawShape(ctx, s, 1, { x: 0, y: 0 });
      }
      const blob = await new Promise<Blob | null>((resolve) => out.toBlob(resolve, 'image/jpeg', 0.92));
      if (!blob) throw new Error('export failed');
      await onSave(blob);
    } catch (e) {
      console.error('[ImageEditor] save failed:', e);
      setSaving(false);
    }
  };

  const showLineStyle = tool === 'line' || tool === 'curve';

  const editingShape = shapes.find((s) => s.id === editingTextId && s.kind === 'text') as TextShape | undefined;
  const editingScreen = editingShape ? screenOf({ x: editingShape.x, y: editingShape.y }) : null;

  const tools: { id: Tool; icon: React.ReactNode; label: string }[] = [
    { id: 'draw', icon: <Pencil className="h-5 w-5" />, label: 'Draw' },
    { id: 'smooth', icon: <Wand2 className="h-5 w-5" />, label: 'Smooth' },
    { id: 'line', icon: <Minus className="h-5 w-5" />, label: 'Line' },
    { id: 'curve', icon: <Spline className="h-5 w-5" />, label: 'Curve' },
    { id: 'text', icon: <Type className="h-5 w-5" />, label: 'Text' },
  ];

  return createPortal(
    <div
      className="fixed inset-0 z-[100300] bg-black flex flex-col select-none"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between px-3 py-2 text-white shrink-0">
        <button onClick={handleCancel} className="px-3 py-1.5 text-[15px] font-medium rounded-full hover:bg-white/10 active:bg-white/20">
          Cancel
        </button>
        <button
          onClick={undo}
          disabled={shapes.length === 0}
          className="h-10 w-10 flex items-center justify-center rounded-full hover:bg-white/10 active:bg-white/20 disabled:opacity-30"
          aria-label="Undo"
        >
          <Undo2 className="h-5 w-5" />
        </button>
        <button
          onClick={handleSave}
          disabled={saving || shapes.length === 0}
          className="px-3 py-1.5 text-[15px] font-semibold text-blue-400 rounded-full hover:bg-white/10 active:bg-white/20 disabled:opacity-40 flex items-center gap-1.5"
        >
          {saving ? <span className="h-4 w-4 border-2 border-blue-400/40 border-t-blue-400 rounded-full animate-spin" /> : <Check className="h-4 w-4" />}
          Done
        </button>
      </div>

      {/* Stage */}
      <div ref={stageRef} className="relative flex-1 min-h-0 overflow-hidden">
        {!imgReady && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-10 w-10 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          </div>
        )}
        <canvas
          ref={canvasRef}
          className="absolute inset-0 touch-none"
          style={{ touchAction: 'none' }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        />
        {editingShape && editingScreen && (
          <textarea
            autoFocus
            value={editingShape.text}
            onChange={(e) => updateText(editingShape.id, e.target.value)}
            onBlur={commitText}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Escape') {
                e.preventDefault();
                (e.target as HTMLTextAreaElement).blur();
              }
            }}
            placeholder="Text"
            rows={1}
            className="absolute outline-none border-2 border-blue-400/80 rounded bg-black/30 px-1 resize-none overflow-hidden whitespace-pre"
            style={{
              left: editingScreen.x,
              top: editingScreen.y,
              color: editingShape.color,
              font: `600 ${Math.max(12, editingShape.size * viewRef.current.scale)}px ${FONT}`,
              lineHeight: 1.2,
              minWidth: 40,
              maxWidth: '80%',
              caretColor: '#fff',
            }}
          />
        )}
      </div>

      {/* Tools panel */}
      <div className="shrink-0 bg-neutral-900/95 backdrop-blur border-t border-white/10 px-3 pt-3 pb-2 space-y-3">
        {/* Line style + arrow (contextual) */}
        {showLineStyle && (
          <div className="flex items-center justify-center gap-2">
            <div className="inline-flex rounded-xl bg-white/10 p-0.5">
              <button
                onClick={() => applyDash(false)}
                className={`px-3.5 py-1.5 rounded-lg text-[13px] font-medium transition-colors ${!dash ? 'bg-white text-black' : 'text-white/70'}`}
              >
                Solid
              </button>
              <button
                onClick={() => applyDash(true)}
                className={`px-3.5 py-1.5 rounded-lg text-[13px] font-medium transition-colors ${dash ? 'bg-white text-black' : 'text-white/70'}`}
              >
                Dashed
              </button>
            </div>
            <div className="inline-flex rounded-xl bg-white/10 p-0.5">
              <button
                onClick={() => applyArrow(false)}
                className={`px-3.5 py-1.5 rounded-lg text-[13px] font-medium transition-colors ${!arrow ? 'bg-white text-black' : 'text-white/70'}`}
              >
                Line
              </button>
              <button
                onClick={() => applyArrow(true)}
                className={`px-3.5 py-1.5 rounded-lg text-[13px] font-medium transition-colors ${arrow ? 'bg-white text-black' : 'text-white/70'}`}
              >
                Arrow
              </button>
            </div>
          </div>
        )}

        {/* Colors + sizes */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 overflow-x-auto">
            {COLORS.map((c) => (
              <button
                key={c}
                onClick={() => applyColor(c)}
                className={`h-7 w-7 shrink-0 rounded-full border transition-transform ${color === c ? 'scale-110 border-white ring-2 ring-white/60' : 'border-white/30'}`}
                style={{ backgroundColor: c }}
                aria-label={`Color ${c}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2 pl-2 shrink-0">
            {[0, 1, 2].map((i) => (
              <button
                key={i}
                onClick={() => applySize(i)}
                className={`h-8 w-8 flex items-center justify-center rounded-full ${sizeIdx === i ? 'bg-white/20' : ''}`}
                aria-label={`Size ${i + 1}`}
              >
                <span className="rounded-full bg-white" style={{ width: `${6 + i * 5}px`, height: `${6 + i * 5}px` }} />
              </button>
            ))}
          </div>
        </div>

        {/* Tool selector */}
        <div className="flex items-center justify-around">
          {tools.map((t) => (
            <button
              key={t.id}
              onClick={() => {
                if (editingTextId != null) commitText();
                setTool(t.id);
                setActiveId(null);
              }}
              className={`flex flex-col items-center gap-1 px-2.5 py-1.5 rounded-xl transition-colors ${tool === t.id ? 'bg-white/15 text-blue-400' : 'text-white/70'}`}
            >
              {t.icon}
              <span className="text-[11px] font-medium">{t.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Discard confirm */}
      {confirmExit && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/60 px-8">
          <div className="w-full max-w-xs rounded-2xl bg-neutral-800 text-white overflow-hidden">
            <div className="p-5 text-center">
              <div className="text-[17px] font-semibold">Discard changes?</div>
              <div className="mt-1 text-[13px] text-white/60">Your edits to this image will be lost.</div>
            </div>
            <div className="grid grid-cols-2 border-t border-white/10">
              <button onClick={() => setConfirmExit(false)} className="py-3 text-[15px] font-medium text-blue-400 border-r border-white/10 active:bg-white/10">
                Keep Editing
              </button>
              <button onClick={onCancel} className="py-3 text-[15px] font-semibold text-red-400 active:bg-white/10 flex items-center justify-center gap-1.5">
                <X className="h-4 w-4" />
                Discard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}
